import React, { createContext, useContext, useMemo } from 'react';
import {
    FollowOrganizerClient,
    FollowClientConfig,
} from '../api/client';

/**
 * Provides the single `FollowOrganizerClient` instance to the follow hooks.
 *
 * This replaces eb-ui's redux-destination store + the `addFollow` HOC's shared
 * React context: instead of a redux store threading follow-state, the client is
 * injected here and react-query owns the state (C1 drop-recipe).
 */
const FollowClientContext = createContext<FollowOrganizerClient | null>(null);

export interface FollowClientProviderProps {
    /** Pass a pre-built client, or a config to build one (memoized). */
    client?: FollowOrganizerClient;
    config?: FollowClientConfig;
    children: React.ReactNode;
}

export const FollowClientProvider = ({
    client,
    config,
    children,
}: FollowClientProviderProps) => {
    const value = useMemo(
        () => client ?? new FollowOrganizerClient(config),
        [client, config],
    );

    return (
        <FollowClientContext.Provider value={value}>
            {children}
        </FollowClientContext.Provider>
    );
};

/**
 * Reads the follow client from context. Throws (fail-loud) if a hook is used
 * outside `FollowClientProvider` — a silent default would hide a mis-wired app.
 */
export const useFollowClient = (): FollowOrganizerClient => {
    const client = useContext(FollowClientContext);
    if (!client) {
        throw new Error(
            'useFollowClient must be used within a <FollowClientProvider>. ' +
                'Wrap your app (below the react-query QueryClientProvider) with it.',
        );
    }
    return client;
};
